/**
 * Друкована форма ТТН (2026-07-28) — ЄДИНИЙ стиль друку проекту
 * (дзеркало CustomerInvoicePrintPage/ServiceActPrintPage): Times, A4,
 * кнопки «друкувати/назад», відкривається у тому ж вікні.
 *
 * Форма — додаток 7 до Правил перевезень вантажів автомобільним
 * транспортом (у редакції наказу Мінрозвитку від 12.12.2025 № 1727,
 * діє з 26.07.2026): «Місце зберігання транспортного засобу», вантаж
 * з рядків реалізації (найменування/к-сть/ціна/сума — відповідальність
 * перевізника), додаткове місце завантаження (догруз).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tnn } from '@/api/tnn';
import { goodsSales } from '@/api/goodsSales';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { amountToWordsUA } from '@/utils/numberToWordsUA';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TnnPrintPage() {
  const { id } = useParams<{ id: string }>();

  // Друк за ЄДИНОЮ механікою друкованих форм (body.print-mode +
  // .print-page): у друк йде ЛИШЕ бланк, без layout застосунку.
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);



  const docQ = useQuery({
    queryKey: ['tnn', id],
    queryFn: () => tnn.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;

  const saleQ = useQuery({
    queryKey: ['goods-sale', d?.goods_sale_id],
    queryFn: () => goodsSales.get(d!.goods_sale_id),
    enabled: !!d?.goods_sale_id,
  });
  const sale = saleQ.data;

  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const consignee = contractorsQ.data?.items.find((c) => c.id === sale?.contractor_id);
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження ТТН…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const items = sale?.items ?? [];
  const total = items.reduce((s, it) => s + Number(it.total_amount), 0);
  const vehicle = [d.vehicle_model, d.vehicle_plate].filter(Boolean).join(' ');

  const req = (label: string, value: string | null | undefined) => (
    value ? (
      <p style={{ margin: '0.2rem 0' }}>
        <span style={{ color: '#333' }}>{label}:</span> <strong>{value}</strong>
      </p>
    ) : null
  );

  return (
    <div className="print-page invoice-print" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          @page { size: A4; margin: 1.2cm; }
        }
        .tnn-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .tnn-table th, .tnn-table td { border: 1px solid #000; padding: 1px 4px; }
        .tnn-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.tnn',
        documentId: d.id,
        filename: `ТТН ${d.document_number}`,
        orgName: org.name,
      }} />

      <h2 style={{ textAlign: 'center', margin: '0.5rem 0 0.2rem' }}>
        ТОВАРНО-ТРАНСПОРТНА НАКЛАДНА № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>
      <p style={{ textAlign: 'center', fontSize: '8.5pt', color: '#333', margin: '0 0 0.75rem' }}>
        Додаток 7 до Правил перевезень вантажів автомобільним транспортом в Україні<br />
        (у редакції наказу Мінрозвитку від 12.12.2025 № 1727)
      </p>

      {req('Автомобіль', vehicle || null)}
      {req('Автомобільний перевізник',
        `${d.carrier_name}${d.carrier_edrpou ? `, ЄДРПОУ ${d.carrier_edrpou}` : ''}`)}
      <p style={{ margin: '0.2rem 0' }}>
        <span style={{ color: '#333' }}>Місце зберігання транспортного засобу:</span>{' '}
        {d.vehicle_storage_address
          ? <strong>{d.vehicle_storage_address}</strong>
          : '________________________________________________'}
      </p>
      {req('Водій',
        `${d.driver_full_name}${d.driver_license_number ? `, посвідчення ${d.driver_license_number}` : ''}`)}

      <div style={{ marginTop: '0.6rem' }}>
        {req('Вантажовідправник',
          `${org.full_name || org.name}${partyCodesText(org) ? `, ${partyCodesText(org)}` : ''}${org.legal_address ? `, ${org.legal_address}` : ''}`)}
        {req('Вантажоодержувач', consignee
          ? `${consignee.name}${partyCodesText(consignee) ? `, ${partyCodesText(consignee)}` : ''}${consignee.legal_address ? `, ${consignee.legal_address}` : ''}`
          : '—')}
        {req('Пункт навантаження', d.shipping_address || org.legal_address || '—')}
        {req('Додаткове місце завантаження (догруз)', d.shipping_address_2)}
        {req('Пункт розвантаження', d.destination_address || consignee?.legal_address || '—')}
      </div>

      <table className="tnn-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування вантажу</th>
            <th style={{ width: 55 }}>Од. вим.</th>
            <th style={{ width: 75 }}>Кількість</th>
            <th style={{ width: 90 }}>Ціна без ПДВ, грн</th>
            <th style={{ width: 100 }}>Загальна сума з ПДВ, грн</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center' }}>—</td></tr>
          )}
          {items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id ?? '');
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  {nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {Number(it.quantity).toLocaleString('uk-UA', { maximumFractionDigits: 3 })}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(it.price)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(it.total_amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p style={{ margin: '0.4rem 0' }}>
        <span style={{ color: '#333' }}>Усього відпущено на загальну суму:</span>{' '}
        <strong>{amountToWordsUA(total)}</strong> ({fmt(total)} грн)
      </p>
      <p style={{ margin: '0.2rem 0' }}>
        <span style={{ color: '#333' }}>Кількість місць:</span> {d.total_places ?? '—'}
        {'   '}<span style={{ color: '#333', marginLeft: 12 }}>Маса брутто, кг:</span> {d.total_weight_kg ?? '—'}
      </p>
      {req('Супровідні документи на вантаж', sale
        ? `видаткова накладна № ${sale.document_number} від ${new Date(sale.document_date).toLocaleDateString('uk-UA')}`
        : null)}
      {req('Вантаж', d.cargo_description)}

      <table style={{ width: '100%', marginTop: 24, fontSize: '10pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '8px 8px 2px 0' }}>
              Відпуск дозволив: _______________________<br />
              <span style={{ fontSize: '8.5pt', color: '#333' }}>(посада, підпис, П.І.Б.)</span>
            </td>
            <td style={{ width: '50%', padding: '8px 0 2px' }}>
              Прийняв водій/експедитор: _______________________<br />
              <span style={{ fontSize: '8.5pt', color: '#333' }}>(підпис, П.І.Б.)</span>
            </td>
          </tr>
          <tr>
            <td style={{ padding: '10px 8px 2px 0' }}>
              Здав водій/експедитор: _______________________<br />
              <span style={{ fontSize: '8.5pt', color: '#333' }}>(підпис, П.І.Б.)</span>
            </td>
            <td style={{ padding: '10px 0 2px' }}>
              Прийняв вантажоодержувач: _______________________<br />
              <span style={{ fontSize: '8.5pt', color: '#333' }}>(посада, підпис, П.І.Б.)</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
